import { TripStatus } from "@safiri/shared";
import type { Bilingual } from "../types/api";

const tzs = new Intl.NumberFormat("en-TZ", {
  style: "currency",
  currency: "TZS",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatMoney(value: number | undefined | null): string {
  if (value === undefined || value === null || Number.isNaN(value)) return "—";
  return tzs.format(value);
}

export function formatNumber(value: number | undefined | null): string {
  if (value === undefined || value === null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat("en-US").format(value);
}

export function formatDateTime(value: string | Date | undefined | null): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDate(value: string | Date | undefined | null): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

// The backend stores some dates as "DD-MM-YYYY" (driver dob + document expiry
// dates). `new Date("08-10-2026")` is unreliable for that format, so parse it
// explicitly; falls back to native parsing for ISO strings / Date objects.
export function parseBackendDate(
  value: string | Date | undefined | null,
): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Public-facing trip reference. Uses the incremental tripRefNumber when present
// (e.g. "T-100123"), otherwise falls back to the last 8 chars of the id.
export function tripRef(t: {
  tripRefNumber?: number;
  _id?: string;
  tripId?: string;
}): string {
  if (t.tripRefNumber) return `T-${t.tripRefNumber}`;
  const id = t._id ?? t.tripId;
  return id ? id.slice(-8) : "—";
}

type FareLike = {
  tripStatus?: string;
  paymentDetail?: {
    pricingDetail?: {
      netFare?: number;
      grossFare?: number;
      driverEarning?: number;
      platformCommission?: number;
    };
  };
  rideTypeDetail?: {
    rideFareDetails?: {
      netFare?: number;
      grossFare?: number;
      driverEarning?: number;
      platformCommission?: number;
    };
  };
};

// The settled fare (paymentDetail.pricingDetail) only exists once a trip is
// actually charged, and can differ from the upfront quote
// (rideTypeDetail.rideFareDetails) taken at booking time — see the
// upfront-pricing corridor in trip.service.ts. Cancelled trips and trips
// where no driver was ever found are never charged, so there's no real fare
// to show; falling back to the quote there reads as a charge that never
// happened. Prefer the settled fare when it exists, else the quote (for
// in-progress/searching trips), else nothing.
export function tripFareDetail(t: FareLike) {
  if (
    t.tripStatus === TripStatus.CANCELLED ||
    t.tripStatus === TripStatus.NOT_FOUND ||
    t.tripStatus === TripStatus.EXPIRED
  )
    return undefined;
  return t.paymentDetail?.pricingDetail ?? t.rideTypeDetail?.rideFareDetails;
}

export function tripFare(t: FareLike): number | undefined {
  return tripFareDetail(t)?.netFare;
}

// Read a bilingual field's English text; tolerate plain strings.
export function en(value: Bilingual | string | undefined | null): string {
  if (!value) return "—";
  if (typeof value === "string") return value;
  return value.en ?? value.sw ?? "—";
}

/** 0.4567 → "45.7%". */
export function formatPercent(value: number | null | undefined, digits = 1): string {
  if (value === undefined || value === null || Number.isNaN(value)) return "—";
  return `${(value * 100).toFixed(digits)}%`;
}

/** Minutes → "4.5 min" / "1 h 20 min". */
export function formatMinutes(value: number | null | undefined): string {
  if (value === undefined || value === null || Number.isNaN(value)) return "—";
  if (value < 60) return `${Math.round(value * 10) / 10} min`;
  const h = Math.floor(value / 60);
  return `${h} h ${Math.round(value - h * 60)} min`;
}

/** Compact TZS for chart axes: 1,250,000 → "1.25M". */
export function formatCompact(value: number | null | undefined): string {
  if (value === undefined || value === null || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(value);
}

/** Relative change between two numbers; null when there's no baseline. */
export function relativeChange(current: number | null | undefined, previous: number | null | undefined): number | null {
  if (current === null || current === undefined || previous === null || previous === undefined) return null;
  if (previous === 0) return current === 0 ? 0 : null;
  return (current - previous) / Math.abs(previous);
}
